import React, { useEffect, useState } from 'react';
import './AcquisitionPage.css';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'openbro_analytics_token';
const FLAGS = { ca: '🇨🇦', us: '🇺🇸', fr: '🇫🇷', uk: '🇬🇧' };
const STEP_LABEL = {
  landing: 'Visite',
  scroll_25: 'Scroll 25 %',
  scroll_50: 'Scroll 50 %',
  scroll_75: 'Scroll 75 %',
  video_started: 'Vidéo lancée',
  video_50: 'Vidéo 50 %',
  video_100: 'Vidéo finie',
  cta: 'CTA',
  checkout: 'Checkout',
  purchase: 'Paiement',
  onboard_start: 'Activation',
  onboard_done: 'Compte créé',
};

function money(cents, currency = 'eur') {
  const amount = (Number(cents) || 0) / 100;
  const shown = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  if (currency === 'usd') return `$${shown}`;
  if (currency === 'cad') return `${shown} $ CA`;
  return `${amount.toFixed(2)} €`;
}

function pct(part, total) {
  if (!total) return '—';
  return `${Math.round((part / total) * 100)} %`;
}

function badge(visits, purchases, spendCents) {
  if (!spendCents) return { tone: 'wait', label: 'Dépense à saisir' };
  if (visits < 100) return { tone: 'wait', label: 'Pas assez de volume' };
  if (!purchases) return { tone: 'stop', label: 'Stop / retravailler' };
  const cac = spendCents / purchases;
  if (cac < 2500) return { tone: 'win', label: 'Winner' };
  if (cac <= 4000) return { tone: 'watch', label: 'À surveiller' };
  return { tone: 'stop', label: 'Stop / retravailler' };
}

export default function AcquisitionPage({ embedded = false }) {
  const [token] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [geo, setGeo] = useState('ca');
  const [euros, setEuros] = useState('');

  const load = async (auth) => {
    const res = await fetch(`${API}/api/analytics/acquisition`, { headers: { Authorization: `Bearer ${auth}` } });
    const body = await res.json();
    if (!res.ok) throw new Error(body.detail || 'Accès refusé');
    setData(body);
  };

  useEffect(() => {
    if (!token) return undefined;
    load(token).catch((err) => setError(err.message));
    const timer = window.setInterval(() => load(token).catch(() => {}), 15000);
    return () => window.clearInterval(timer);
  }, [token]);

  const saveSpend = async (event) => {
    event.preventDefault();
    const cents = Math.round(Number(String(euros).replace(',', '.')) * 100);
    if (!Number.isFinite(cents)) return;
    await fetch(`${API}/api/analytics/spend`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ geo, cents }),
    });
    setEuros('');
    load(token).catch((err) => setError(err.message));
  };

  if (!token) return null;

  const rows = data?.rows || [];
  const spend = data?.spend || {};
  const sum = (key) => rows.reduce((total, row) => total + (row[key] || 0), 0);
  const visits = sum('landing');
  const checkouts = sum('checkout');
  const purchases = sum('purchase');
  const spendCents = Object.values(spend).reduce((total, value) => total + (Number(value) || 0), 0);
  const revenue = {};
  rows.forEach((row) => {
    Object.entries(row.revenue || {}).forEach(([currency, cents]) => {
      revenue[currency] = (revenue[currency] || 0) + cents;
    });
  });
  const buttons = {};
  rows.forEach((row) => {
    Object.entries(row.buttons || {}).forEach(([button, count]) => {
      buttons[button] = (buttons[button] || 0) + count;
    });
  });
  const flow = ['landing', 'scroll_50', 'cta', 'checkout', 'purchase'];

  return (
    <div className={embedded ? 'aq aq-in' : 'aq'}>
      <header className="aq-head">
        <div>
          <p className="aq-kicker">Acquisition · {data?.day || 'aujourd’hui'}</p>
          <h1>D’où vient chaque vente</h1>
        </div>
      </header>
      {error && <p className="aq-error">{error}</p>}
      <section className="aq-kpis">
        {[
          ['Dépense', spendCents ? money(spendCents) : '—'],
          ['Visites suivies', visits],
          ['Coût / visite', visits && spendCents ? money(Math.round(spendCents / visits)) : '—'],
          ['Checkouts', checkouts],
          ['Achats', purchases],
          ['CA', Object.entries(revenue).map(([currency, cents]) => money(cents, currency)).join(' · ') || '0 €'],
          ['CAC', purchases && spendCents ? money(Math.round(spendCents / purchases)) : '—'],
          ['Conversion', pct(purchases, visits)],
        ].map(([label, value]) => (
          <article key={label}><span>{label}</span><b>{value}</b></article>
        ))}
      </section>
      <section className="aq-flow">
        {flow.map((step, index) => (
          <article key={step}>
            <b>{sum(step)}</b>
            <span>{STEP_LABEL[step]}</span>
            {index > 0 && <em>{pct(sum(step), sum(flow[index - 1]))}</em>}
          </article>
        ))}
      </section>
      <section className="aq-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Audience</th><th>Créa</th><th>Pays réel</th><th>Visites</th><th>50 %</th><th>CTA</th><th>Checkout</th><th>Achat</th><th>Conv.</th><th>CA</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="11">Aucune visite avec attribution aujourd’hui. Les pubs doivent utiliser une URL avec utm_source, utm_campaign, utm_content et utm_term.</td></tr>}
            {rows.map((row) => {
              const mark = badge(row.landing || 0, row.purchase || 0, spend[(row.audience || '').toLowerCase()] || 0);
              return (
                <tr key={`${row.audience}-${row.creative}-${row.country}-${row.source}`}>
                  <td>{FLAGS[row.audience] || ''} {row.audience}</td>
                  <td>{row.creative}</td>
                  <td>{row.country}</td>
                  <td>{row.landing || 0}</td>
                  <td>{row.scroll_50 || 0}</td>
                  <td>{row.cta || 0}</td>
                  <td>{row.checkout || 0}</td>
                  <td>{row.purchase || 0}</td>
                  <td>{pct(row.purchase || 0, row.landing || 0)}</td>
                  <td>{Object.entries(row.revenue || {}).map(([currency, cents]) => money(cents, currency)).join(' · ') || '—'}</td>
                  <td><i className={`aq-${mark.tone}`}>{mark.label}</i></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <section className="aq-tools">
        <form onSubmit={saveSpend}>
          <h2>Dépense du jour</h2>
          <select value={geo} onChange={(event) => setGeo(event.target.value)}>
            <option value="ca">Canada</option>
            <option value="us">USA</option>
            <option value="fr">France</option>
            <option value="uk">UK</option>
          </select>
          <input value={euros} onChange={(event) => setEuros(event.target.value)} inputMode="decimal" placeholder="82" />
          <button type="submit">Enregistrer €</button>
        </form>
        <div>
          <h2>Quel bouton a convaincu</h2>
          <ul>
            {Object.keys(buttons).length === 0 && <li>Pas encore de clic.</li>}
            {Object.entries(buttons).sort((a, b) => b[1] - a[1]).map(([button, count]) => <li key={button}>{button} · {count}</li>)}
          </ul>
        </div>
        <p className="aq-note">Chaque visite se revoit dans l’onglet Personnes. Une vente garde sa source dans Stripe, jusque sur l’abonnement.</p>
      </section>
    </div>
  );
}
